import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type UseVerticalDragProps = {
  onClose: () => void;
  threshold?: number;
  disableDismiss?: boolean;
  snapTranslatePoints?: number[];
  initialTranslateY?: number;
  closeThreshold?: number;
  velocityCloseThreshold?: number;
  velocitySnapThreshold?: number;
  onSnapTranslate?: (translateY: number) => void;
};

type PointerHandlers = {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
};

type UseVerticalDragResult = {
  bind: PointerHandlers;
  translateY: number;
  isDragging: boolean;
};

const RESISTANCE_FACTOR = 0.6;
const DEFAULT_VELOCITY_CLOSE = 0.5; // px/ms (~500px/s)
const DEFAULT_VELOCITY_SNAP = 0.22;
const SNAP_DIRECTION_DISTANCE_BIAS = 24;

function nearestValue(values: number[], current: number): number {
  if (values.length === 0) return current;
  return values.reduce((best, candidate) =>
    Math.abs(candidate - current) < Math.abs(best - current) ? candidate : best
  );
}

function nextLowerSnap(values: number[], current: number): number {
  const candidates = values.filter((v) => v > current);
  return candidates.length > 0 ? candidates[0] : values[values.length - 1];
}

function nextHigherSnap(values: number[], current: number): number {
  const candidates = values.filter((v) => v < current);
  return candidates.length > 0 ? candidates[candidates.length - 1] : values[0];
}

export function useVerticalDrag({
  onClose,
  threshold = 120,
  disableDismiss = false,
  snapTranslatePoints,
  initialTranslateY = 0,
  closeThreshold,
  velocityCloseThreshold = DEFAULT_VELOCITY_CLOSE,
  velocitySnapThreshold = DEFAULT_VELOCITY_SNAP,
  onSnapTranslate,
}: UseVerticalDragProps): UseVerticalDragResult {
  const [startY, setStartY] = useState<number | null>(null);
  const [translateY, setTranslateY] = useState(initialTranslateY);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartTranslateRef = useRef(initialTranslateY);
  const lastYRef = useRef(0);
  const lastTsRef = useRef(0);
  const velocityRef = useRef(0);
  const activePointerIdRef = useRef<number | null>(null);

  const snapTargets = useMemo(
    () =>
      (snapTranslatePoints ?? [])
        .filter((n) => Number.isFinite(n) && n >= 0)
        .sort((a, b) => a - b),
    [snapTranslatePoints]
  );
  const hasSnapPoints = snapTargets.length > 0;
  const minTranslate = hasSnapPoints ? snapTargets[0] : 0;
  const maxTranslate = hasSnapPoints ? snapTargets[snapTargets.length - 1] : Number.POSITIVE_INFINITY;
  const RESISTANCE_START = hasSnapPoints ? maxTranslate : threshold;

  useEffect(() => {
    if (isDragging) return;
    setTranslateY(initialTranslateY);
  }, [initialTranslateY, isDragging]);

  useEffect(() => {
    if (!hasSnapPoints || isDragging) return;
    const snapped = nearestValue(snapTargets, translateY);
    if (snapped !== translateY) setTranslateY(snapped);
  }, [hasSnapPoints, isDragging, snapTargets, translateY]);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;
    const canStartDrag = target?.closest('[data-drag-handle="true"]') != null;
    if (!canStartDrag) return;
    const root = event.currentTarget as HTMLElement;
    const scrollContainer = root.querySelector('[data-bottom-sheet-scroll="true"]') as HTMLElement | null;
    if (scrollContainer && scrollContainer.scrollTop > 0) return;
    activePointerIdRef.current = event.pointerId;
    lastYRef.current = event.clientY;
    lastTsRef.current = event.timeStamp;
    velocityRef.current = 0;
    dragStartTranslateRef.current = translateY;
    setStartY(event.clientY);
    setIsDragging(true);
  }, [translateY]);

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (activePointerIdRef.current !== event.pointerId) return;
      if (!isDragging || startY == null) return;
      const deltaY = event.clientY - startY;
      const dt = Math.max(1, event.timeStamp - lastTsRef.current);
      const dy = event.clientY - lastYRef.current;
      velocityRef.current = dy / dt;
      lastYRef.current = event.clientY;
      lastTsRef.current = event.timeStamp;

      let raw = dragStartTranslateRef.current + deltaY;
      if (raw < minTranslate) {
        raw = minTranslate - (minTranslate - raw) * RESISTANCE_FACTOR;
      } else if (Number.isFinite(maxTranslate) && raw > maxTranslate) {
        raw = maxTranslate + (raw - maxTranslate) * RESISTANCE_FACTOR;
      } else if (!Number.isFinite(maxTranslate) && raw > RESISTANCE_START) {
        raw = RESISTANCE_START + (raw - RESISTANCE_START) * RESISTANCE_FACTOR;
      }
      setTranslateY(Math.max(0, raw));
    },
    [isDragging, startY, minTranslate, maxTranslate, RESISTANCE_START]
  );

  const endDrag = useCallback((forceClose: boolean) => {
    if (!isDragging) return;
    const closeLimit =
      closeThreshold ??
      (Number.isFinite(maxTranslate) ? maxTranslate + threshold : threshold);
    const shouldCloseByGesture =
      translateY > closeLimit || velocityRef.current > velocityCloseThreshold;

    const shouldClose = !disableDismiss && (forceClose || shouldCloseByGesture);
    if (shouldClose) {
      onClose();
    } else if (hasSnapPoints) {
      const nearest = nearestValue(snapTargets, translateY);
      const dragDelta = translateY - dragStartTranslateRef.current;
      let snapped = nearest;

      // Directional bias: downward drag prefers lower snap (larger translateY),
      // upward drag prefers higher snap (smaller translateY).
      if (
        velocityRef.current > velocitySnapThreshold ||
        dragDelta > SNAP_DIRECTION_DISTANCE_BIAS
      ) {
        snapped = nextLowerSnap(snapTargets, nearest);
      } else if (
        velocityRef.current < -velocitySnapThreshold ||
        dragDelta < -SNAP_DIRECTION_DISTANCE_BIAS
      ) {
        snapped = nextHigherSnap(snapTargets, nearest);
      }

      setTranslateY(snapped);
      onSnapTranslate?.(snapped);
    }

    activePointerIdRef.current = null;
    lastYRef.current = 0;
    lastTsRef.current = 0;
    velocityRef.current = 0;
    setStartY(null);
    setIsDragging(false);
    if (!hasSnapPoints) {
      setTranslateY(0);
      onSnapTranslate?.(0);
    }
  }, [
    isDragging,
    closeThreshold,
    maxTranslate,
    threshold,
    translateY,
    velocityCloseThreshold,
    disableDismiss,
    onClose,
    hasSnapPoints,
    snapTargets,
    velocitySnapThreshold,
    onSnapTranslate,
  ]);

  const onPointerUp = useCallback(() => {
    endDrag(false);
  }, [endDrag]);

  const onPointerCancel = useCallback(() => {
    endDrag(false);
  }, [endDrag]);

  return {
    bind: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
    translateY,
    isDragging,
  };
}

